import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Check, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Avatar, Button, PageHeader } from '../../components/ui';
import { userAPI } from '../../lib/api';
import { PARTY_CHOICES, PRIORITY_CHOICES } from '../../lib/intakeChoices';
import paulaAvatar from '../../assets/paula.png';

// Scripted conversational intake that produces the same data as quick setup.
// The script is local for now; the AI interpretation layer (subject
// classification, entity extraction) is a planned backend feature.

interface ScriptMessage {
  from: 'assistant' | 'user' | 'user-zip' | 'user-priorities' | 'user-party' | 'finish';
  text?: string;
}

export default function IntakeConvoScreen() {
  const navigate = useNavigate();

  const SCRIPT = useMemo<ScriptMessage[]>(
    () => [
      {
        from: 'assistant',
        text: "Hi — I'm Paula, your Capitol Commons guide. Let's set up your civic profile. Takes about a minute.",
      },
      { from: 'assistant', text: "First, what ZIP code do you vote from? I'll figure out your districts." },
      { from: 'user-zip' },
      {
        from: 'assistant',
        text: 'Got it — saved.\n\nWhich of these issues do you care about most? Pick any that apply.',
      },
      { from: 'user-priorities' },
      {
        from: 'assistant',
        text: "Thanks. We'll surface issues and hearings that touch those.\n\nOne more — how are you registered?",
      },
      { from: 'user-party' },
      {
        from: 'assistant',
        text: "Got it. You're all set — I'll route briefings, ballot items, and town halls relevant to you. You can always tweak this in your Profile.",
      },
      { from: 'finish' },
    ],
    []
  );

  const [stepIdx, setStepIdx] = useState(2); // first two messages already shown
  const [visible, setVisible] = useState<ScriptMessage[]>([SCRIPT[0]!, SCRIPT[1]!]);
  const [typing, setTyping] = useState(false);
  const [zip, setZip] = useState('');
  const [priorities, setPriorities] = useState<string[]>([]);
  const [, setParty] = useState('');
  const [saving, setSaving] = useState(false);
  const partyRef = useRef('');
  const stepIdxRef = useRef(stepIdx);
  const scrollRef = useRef<HTMLDivElement>(null);

  function advance() {
    const next = SCRIPT[stepIdxRef.current + 1];
    if (!next) return;
    stepIdxRef.current += 1;
    setStepIdx(stepIdxRef.current);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setVisible((v) => [...v, next]);
    }, 700);
  }

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [visible, typing]);

  function handleZipSubmit() {
    if (zip.length < 5) return;
    setVisible((v) => [...v, { from: 'user', text: zip }]);
    advance();
  }

  function handlePrioritiesSubmit() {
    if (priorities.length === 0) return;
    setVisible((v) => [...v, { from: 'user', text: priorities.join(', ') }]);
    advance();
  }

  function handlePartySubmit(id: string) {
    const label = PARTY_CHOICES.find((p) => p.id === id)?.label || id;
    setParty(id);
    partyRef.current = id;
    setVisible((v) => [...v, { from: 'user', text: label }]);
    advance();
  }

  async function finish() {
    if (saving) return;
    setSaving(true);
    try {
      await userAPI.updateProfile({
        zipCode: zip,
        interests: priorities,
        politicalAffiliation: partyRef.current || null,
      });
      // Best-effort district resolution from the ZIP (see quick intake).
      try {
        await userAPI.resolveDistricts(zip);
      } catch {
        /* districts can be set later from Profile */
      }
      navigate('/citizen/intake/success');
    } catch {
      toast.error('Could not save your profile. Please try again.');
      setSaving(false);
    }
  }

  const pending = SCRIPT[stepIdx];

  return (
    <div className="flex flex-col min-h-screen screen-enter">
      <PageHeader title="Onboarding" onBack={() => navigate('/citizen/intake')} />

      <div className="flex-1 overflow-y-auto px-4 pt-2 pb-4">
        <div className="text-center mb-4">
          <span className="text-xs text-[color:var(--muted-foreground)] bg-[var(--secondary)] px-3 py-1 rounded-full">
            Today, {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>

        <div className="space-y-4">
          {visible.map((m, i) => (
            <ChatBubble key={i} m={m} />
          ))}
          {typing && <TypingBubble />}
          <div ref={scrollRef} />
        </div>

        {/* Pending interactive controls */}
        {!typing && pending?.from === 'user-zip' && (
          <div className="ml-12 mt-2 anim-slide flex gap-2">
            <input
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
              inputMode="numeric"
              placeholder="ZIP"
              className="flex-1 rounded-full px-4 py-2 text-sm font-medium tabular-nums text-center"
              style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
            />
            <Button size="sm" onClick={handleZipSubmit} disabled={zip.length < 5}>
              Send <Send className="w-3 h-3" />
            </Button>
          </div>
        )}

        {!typing && pending?.from === 'user-priorities' && (
          <div className="ml-12 mt-2 anim-slide space-y-3">
            <div className="flex flex-wrap gap-2">
              {PRIORITY_CHOICES.slice(0, 8).map((p) => {
                const on = priorities.includes(p);
                return (
                  <button
                    key={p}
                    onClick={() => setPriorities(on ? priorities.filter((x) => x !== p) : [...priorities, p])}
                    className="rounded-full px-3 py-1.5 text-xs font-semibold border hover-elevate"
                    style={
                      on
                        ? { background: 'var(--primary)', color: 'white', borderColor: 'var(--primary)' }
                        : { background: 'var(--card)', color: 'var(--primary)', borderColor: 'var(--accent)' }
                    }
                  >
                    {on && <Check className="w-3 h-3 inline mr-1 -mt-0.5" />}
                    {p}
                  </button>
                );
              })}
            </div>
            <Button size="sm" onClick={handlePrioritiesSubmit} disabled={priorities.length === 0}>
              Confirm {priorities.length > 0 && `(${priorities.length})`}
            </Button>
          </div>
        )}

        {!typing && pending?.from === 'user-party' && (
          <div className="ml-12 mt-2 anim-slide flex flex-wrap gap-2">
            {PARTY_CHOICES.slice(0, 4).map((opt) => (
              <button
                key={opt.id}
                onClick={() => handlePartySubmit(opt.id)}
                className="rounded-full px-3 py-1.5 text-xs font-semibold border hover-elevate"
                style={{ background: 'var(--card)', color: 'var(--primary)', borderColor: 'var(--accent)' }}
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 -mt-0.5" style={{ background: opt.color }} />
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {!typing && pending?.from === 'finish' && (
          <div className="ml-12 mt-4 anim-slide">
            <Button onClick={finish} disabled={saving}>
              {saving ? 'Saving…' : 'Open my app'} <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Auto-advance assistant messages */}
      {!typing && pending?.from === 'assistant' && <AdvanceTrigger onTick={advance} />}

      <div className="px-4 pb-4 pt-2">
        <p className="text-[11px] text-center text-[color:var(--muted-foreground)]">
          You can review and tweak everything in Profile later.
        </p>
      </div>
    </div>
  );
}

function ChatBubble({ m }: { m: ScriptMessage }) {
  if (m.from === 'user') {
    return (
      <div className="flex justify-end anim-slide">
        <div
          className="max-w-[80%] rounded-2xl rounded-br-sm px-4 py-2.5 text-sm leading-relaxed"
          style={{ background: 'var(--primary)', color: 'white' }}
        >
          {m.text}
        </div>
      </div>
    );
  }
  if (m.from === 'assistant') {
    return (
      <div className="flex items-end gap-2 anim-slide">
        <Avatar initials="P" size={32} tone="dark" src={paulaAvatar} />
        <div
          className="max-w-[80%] rounded-2xl rounded-bl-sm px-4 py-3 text-sm leading-relaxed whitespace-pre-line border"
          style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
        >
          {m.text}
        </div>
      </div>
    );
  }
  return null;
}

function TypingBubble() {
  return (
    <div className="flex items-end gap-2 anim-slide">
      <Avatar initials="P" size={32} tone="dark" src={paulaAvatar} />
      <div
        className="rounded-2xl rounded-bl-sm px-4 py-3 border flex gap-1"
        style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <Dot delay={0} />
        <Dot delay={120} />
        <Dot delay={240} />
      </div>
    </div>
  );
}

function Dot({ delay }: { delay: number }) {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full bg-[color:var(--muted-foreground)]"
      style={{ animation: `pulse-ping 1.4s ${delay}ms infinite` }}
    />
  );
}

function AdvanceTrigger({ onTick }: { onTick: () => void }) {
  useEffect(() => {
    const t = setTimeout(onTick, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}
